import {
  selectLineItemByType,
  selectConfigItemByType
} from '../../selectors/configuration'
import { getConfigItem } from '../configuration'
import {
  internetLineItemTypes as lineTypes,
  internetConfigurationItems,
  staticIpJustificationCounts,
  defaultIPSelection,
  validSecurityEdgeEquipmentIds,
  eligibilityOperators,
  eligibilityFields,
  eligibilityConditions
} from '../../constants/configuration'
import { isValidEmail } from './core'
import { selectCustomerDetails } from '../../selectors/customer'
import errors from '../../constants/errors/internet'

export default {
  validations: {
    securityEdgeEmail: [
      {
        validation: validateSecurityEdgeEmail,
        error: errors.validateSecurityEdgeEmailError
      }
    ],
    securityEdgeOnSave: [
      {
        validation: validateSecurityEdgeOnSave,
        error: errors.validateSecurityEdgeErrorOnSave
      }
    ],
    ipJustifications: [
      {
        validation: checkStaticIpOptionsSet,
        error: errors.checkStaticIpOptionsSetError
      },
      {
        validation: checkOtherIpValue,
        error: errors.checkOtherIpValueError
      }
    ]
  }
}

function getSelectedCartItemIds(categories = {}) {
  return Object.values(categories).reduce((ids, category = {}) => {
    const selectedItems = category.selectedLineItems || []

    return [...ids, ...selectedItems.map(item => item.id)]
  }, [])
}

function getExistingCartItemIds(existingServices = {}) {
  return Object.values(existingServices.productDetails || {})
    .flatMap(({ cartItemDetails = [] } = {}) => cartItemDetails)
    .map(item => item.shoppingElementId)
}

function resolveRuleValue(
  value,
  { existingServices = {}, totalMonthlyCharges = 0 } = {}
) {
  if (typeof value !== 'string') {
    return value
  }
  const normalizedValue = value.trim()
  if (normalizedValue === eligibilityFields.EXISTING_MRC) {
    return existingServices?.totalMrcPrice
  }
  if (normalizedValue === eligibilityFields.MRC_DIFFERENCE) {
    return totalMonthlyCharges
  }
  return value
}

function evaluateOperator(operator, actualValue, values = []) {
  const actualValues = Array.isArray(actualValue) ? actualValue : [actualValue]
  const [comparisonValue] = values

  switch (operator) {
    case eligibilityOperators.IN:
      return actualValues.some(value => values.includes(value))

    case eligibilityOperators.NOT_IN:
      return actualValues.every(value => !values.includes(value))

    case eligibilityOperators.GREATER_THAN:
      return actualValues.every(
        value => Number(value) > Number(comparisonValue)
      )

    case eligibilityOperators.GREATER_OR_EQUAL:
      return actualValues.every(
        value => Number(value) >= Number(comparisonValue)
      )

    case eligibilityOperators.LESS_THAN:
      return actualValues.every(
        value => Number(value) < Number(comparisonValue)
      )

    case eligibilityOperators.LESS_OR_EQUAL:
      return actualValues.every(
        value => Number(value) <= Number(comparisonValue)
      )

    case eligibilityOperators.EQUAL:
      return actualValues.every(value => value === comparisonValue)

    case eligibilityOperators.NOT_EQUAL:
      return actualValues.every(value => value !== comparisonValue)

    default:
      return false
  }
}

function getRuleValues({
  field,
  categories,
  existingServices,
  totalMonthlyCharges
}) {
  if (field === eligibilityFields.CURRENT_CART_ITEM) {
    return getSelectedCartItemIds(categories)
  }

  if (field === eligibilityFields.EXISTING_CART_ITEM) {
    return getExistingCartItemIds(existingServices)
  }

  if (field === eligibilityFields.MRC_DIFFERENCE) {
    return [totalMonthlyCharges]
  }

  return []
}

function evaluateRule({ field, operator, values = [] }, context) {
  const actualValues = getRuleValues({
    field,
    ...context
  })

  if (field === eligibilityFields.MRC_DIFFERENCE && context.isAddendumSelected) 
  {
    const [actualValue] = actualValues
    return Number(actualValue) > 0
  }

  const comparisonValues = values.map(value =>
    resolveRuleValue(value, context)
  )

  return evaluateOperator(operator, actualValues, comparisonValues)
}

function evaluateCondition(condition, rules = [], context) {
  const results = rules.map(rule => evaluateRule(rule, context))

  if (condition === eligibilityConditions.OR) {
    return results.some(Boolean)
  }

  return results.every(Boolean)
}

function getEligibilityPriority({ ruleId, appliesTo }) {
  if (appliesTo === 'existingCart') {
    if (ruleId === 'NO_EXISTING_SEP_OR_WC') {
      return 0
    }
    if (ruleId === 'MRC_GREATER_THAN_EXISTING_MRC') {
      return 1
    }
  }
  if (appliesTo === 'currentCart') {
    return 2
  }
  return 3
}

export function getEpcEligibilityError(
  {
    categories = {},
    existingServices = {},
    totalMonthlyCharges = 0,
    isAddendumSelected = false
  } = {},
  eligibility = []
) {
  const context = {
    categories,
    existingServices,
    totalMonthlyCharges,
    isAddendumSelected
  }

  const eligibilityRules = [...eligibility].sort(
    (firstRule, secondRule) =>
      getEligibilityPriority(firstRule) -
    getEligibilityPriority(secondRule)
  )

  const failedRule = eligibilityRules.find(
    ({ condition = eligibilityConditions.AND, rules = [] }) =>
      !evaluateCondition(condition, rules, context)
  )

  return failedRule?.errorMessage || null
}

export function validateCustomerOwnedEquipment(state, item) {
  const securityEdge = selectLineItemByType(lineTypes.securityEdge)(state)
  const securityEdgeConfigItem = getConfigItem(securityEdge) || {}

  return (
    securityEdgeConfigItem.isSelected &&
    item.id === internetConfigurationItems.customerOwnedEquipment
  )
}

export function checkStaticIpOptionsSet(state) {
  const staticIps = selectLineItemByType(lineTypes.staticIps)(state)
  const staticIpsConfigItem = getConfigItem(staticIps)

  if (!staticIpsConfigItem) {
    return false
  }

  const { id, attributes = {} } = staticIpsConfigItem
  const ipCount = staticIpJustificationCounts[id]
  const ips = attributes.staticIpJustifications || []
  const hasEmptyValue =
    ips.some(j => j === defaultIPSelection) || !ips.length

  return ipCount > 1 && hasEmptyValue
}

export function checkOtherIpValue(state) {
  const staticIps = selectLineItemByType(lineTypes.staticIps)(state)
  const staticIpsConfigItem = getConfigItem(staticIps)

  if (!staticIpsConfigItem) {
    return false
  }

  const { id, attributes = {} } = staticIpsConfigItem
  const ipCount = staticIpJustificationCounts[id]
  const ips = attributes.staticIpJustifications || []

  const noOtherValue = ips.some(j => {
    const hasOtherValue = j
      .trim()
      .toLowerCase()
      .includes('other')

    return hasOtherValue || !j.trim().length
  })

  return ipCount > 1 && noOtherValue
}

export function validateSecurityEdgeEmail(state) {
  const { contact } = selectCustomerDetails(state)
  const { hasOptedOutOfEmail, email } = contact

  const securityEdge = selectLineItemByType(lineTypes.securityEdge)(state)
  const securityEdgeConfigItem = getConfigItem(securityEdge)

  return (
    (hasOptedOutOfEmail || !isValidEmail(email)) &&
    securityEdgeConfigItem?.isSelected
  )
}

export function validateSecurityEdgeOnSave(state) {
  const securityEdgeConfigItem = selectConfigItemByType(
    lineTypes.securityEdge
  )(state)

  const equipment = selectConfigItemByType(
    lineTypes.internetEquipment
  )(state)

  return (
    !!equipment &&
    securityEdgeConfigItem?.isSelected &&
    !validSecurityEdgeEquipmentIds.includes(equipment?.id)
  )
}
